import AdminDesign from "~/components/Admin"
import { useCallback, useContext, useState } from "react";
import { GeneralContext } from "~/contexts";
import api from "~/services/api";
import './styles.css';
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "~/components/Default/Loading";
import dates from "~/utils/dates";
import { Tooltip } from "@mui/material";

interface PreliminaryReportData {
    id: number;
    unit_id: number;
    year: number;
    month: number;
    calculation_status?: string;
}

interface ReportItem {
    id: string; // synthetic id `${year}-${month}`
    year: number;
    month: number;
    free?: PreliminaryReportData;
    captive?: PreliminaryReportData;
    inputData?: any;
}


const ManagerReport = () => {
    const { unit } = useContext(GeneralContext);
    const { user } = useContext(GeneralContext);
    const navigate = useNavigate();
    const [ready, setReady] = useState(false);
    const [reports, setReports] = useState<ReportItem[]>([]);
    const [billsMap, setBillsMap] = useState<{ [yyyymm: string]: boolean }>({});

    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const availableYears = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i);

    const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const year = parseInt(event.target.value);
        setSelectedYear(year);
        // getReports(year);
    };

    const handleAccessClick = useCallback((month: number, isDefinitive: boolean) => {
        const ym = `${selectedYear}-${String(month).padStart(2, '0')}`;
        const type = isDefinitive ? 'definitive' : 'preliminary';
        // Keep year-month in URL; keep type in navigation state (simple/compatible with old behavior)
        navigate(`/relatorio/${ym}`, {
            state: {
                reportType: type,
                month,
                year: selectedYear,
            }
        });
    }, [navigate, selectedYear]);

    const getReports = useCallback(async (year: number) => {
        setReady(false);
        try {
            const [reportsResp, billsResp] = await Promise.all([
                api.get(`/preliminary-reports/unit/${unit.id}`, {
                    headers: { Authorization: user.token }
                }),
                api.get(`bills/${unit.id}`, {
                    headers: { Authorization: user.token }
                })
            ]);

            const apiResponse = reportsResp?.data;
            const list = apiResponse?.data?.reports ?? [];

            const normalized: ReportItem[] = list.map((entry: any) => {
                const free: PreliminaryReportData | undefined = entry.preliminaryReportFree;
                const captive: PreliminaryReportData | undefined = entry.preliminaryReportCaptive;
                const y = free?.year ?? captive?.year ?? year;
                const m = free?.month ?? captive?.month ?? 1;
                return {
                    id: `${y}-${String(m).padStart(2, '0')}`,
                    year: y,
                    month: m,
                    free,
                    captive,
                    inputData: entry.inputData
                };
            });

            setReports(normalized);

            // Bills map by YYYY-MM
            const bills = billsResp?.data ?? [];
            const byMonth: { [k: string]: boolean } = {};
            bills.forEach((b: any) => {
                const dateStr: string = b.reference_date || b.date || '';
                if (!dateStr) return;
                const d = new Date(dateStr);
                const yBill = d.getUTCFullYear();
                const mBill = d.getUTCMonth() + 1;
                const key = `${yBill}-${String(mBill).padStart(2, '0')}`;
                byMonth[key] = true;
            });
            setBillsMap(byMonth);
        } catch (error) {
            console.error('Erro ao carregar relatórios:', error);
            setReports([]);
            setBillsMap({});
        } finally {
            setReady(true);
        }
    }, [user.token, unit?.id]);

    useEffect(() => {
        getReports(selectedYear);
    }, [selectedYear, getReports]);

    // Tipo do relatório é derivado diretamente no render:
    // - Se houver fatura: DEFINITIVO
    // - Sem fatura: PRELIMINAR (mesmo que não haja cálculo preliminar)

    return (
        <AdminDesign
            pageTitle="Gerenciador de Relatórios"
            title="Relatórios"
            classMain="report">
            <div className="header">
                <div className='title'>
                    <h1>{unit.name}</h1>
                </div>

                <div className="buttons">
                    <div className="months">
                        <p>Ano</p>
                        <select
                            name="year"
                            id="year"
                            value={selectedYear}
                            onChange={handleYearChange}
                        >
                            {availableYears.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {
                ready ? (
                    <>
                        <div className="reportTable">
                            <table className="table table--reports">
                                <thead>
                                    <tr>
                                        <th>Mês</th>
                                        <th>Tipo</th>
                                        <th className="col-action">Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(selectedYear === new Date().getFullYear()
                                        ? Array.from({ length: new Date().getMonth() + 1 }, (_, i) => (new Date().getMonth() + 1) - i)
                                        : Array.from({ length: 12 }, (_, i) => 12 - i)
                                    ).map((month) => {
                                        const key = `${selectedYear}-${String(month).padStart(2, '0')}`;
                                        const monthLabel = dates._getNameMonth(month, true);
                                        const hasBill = !!billsMap[key];
                                        const reportForMonth = reports.find(r => r.year === selectedYear && r.month === month);
                                        const hasPreliminary = !!(reportForMonth?.free || reportForMonth?.captive);
                                        const isDefinitive = hasBill || false; // fatura prioriza
                                        const typeLabel = isDefinitive ? 'DEFINITIVO' : 'PRELIMINAR';
                                        const typeClass = isDefinitive ? 'pill--definitive' : 'pill--preliminary';

                                        return (
                                            <tr key={`report_${key}`}>
                                                <td>{monthLabel}</td>
                                                <td>
                                                    <Tooltip title={typeLabel === 'DEFINITIVO'
                                                        ? 'Relatório definitivo é gerado a partir dos dados da fatura'
                                                        : 'Relatório preliminar é gerado a partir de dados de estimativa'}>
                                                        <span className={`pill ${typeClass}`}>{typeLabel}</span>
                                                    </Tooltip>
                                                </td>
                                                <td className="col-action">
                                                { isDefinitive || hasPreliminary ? (
                                                    <button
                                                        onClick={() => handleAccessClick(month, isDefinitive)}
                                                        className="table-action"
                                                    >
                                                        Acessar
                                                    </button>
                                                ) : (
                                                    <Tooltip title="Dados para gerar relatório pendentes, consulte nosso suporte">
                                                        <button className="table-action-generate" disabled={true} >Gerar</button>
                                                    </Tooltip>
                                                )}
                                                
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    <Loading />
                )
            }
        </AdminDesign>
    );
};

export default ManagerReport;